import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Truck, CheckCircle, Clock, Search, Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

var API = "/api";

function formatPrice(price) {
  if (price === null || price === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(price);
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch (e) {
    return "—";
  }
}

function carrierTrackingUrl(carrier, trackingNumber) {
  if (!trackingNumber) return null;
  var c = (carrier || "").toUpperCase();
  if (c.indexOf("USPS") !== -1) return "https://tools.usps.com/go/TrackConfirmAction?tLabels=" + encodeURIComponent(trackingNumber);
  if (c.indexOf("UPS") !== -1) return "https://www.ups.com/track?tracknum=" + encodeURIComponent(trackingNumber);
  if (c.indexOf("FEDEX") !== -1) return "https://www.fedex.com/fedextrack/?trknbr=" + encodeURIComponent(trackingNumber);
  if (c.indexOf("DHL") !== -1) return "https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=" + encodeURIComponent(trackingNumber);
  return null;
}

function StatusBadge({ status }) {
  var s = (status || "").toLowerCase();
  var label = status || "Pending";
  var bg = "bg-[#C0A062]/15 text-[#C0A062] border-[#C0A062]/30";
  var Icon = Clock;
  if (s === "completed") { bg = "bg-blue-500/15 text-blue-300 border-blue-500/30"; Icon = CheckCircle; label = "Paid · Awaiting Shipment"; }
  if (s === "shipped") { bg = "bg-[#8CB369]/15 text-[#8CB369] border-[#8CB369]/30"; Icon = Truck; label = "Shipped"; }
  if (s === "refunded" || s === "partial_refunded") { bg = "bg-orange-500/15 text-orange-300 border-orange-500/30"; Icon = AlertCircle; label = s === "refunded" ? "Refunded" : "Partial Refund"; }
  if (s === "voided" || s === "cancelled") { bg = "bg-red-500/15 text-red-300 border-red-500/30"; Icon = AlertCircle; label = s === "voided" ? "Voided" : "Cancelled"; }
  if (s === "payment_failed") { bg = "bg-red-500/15 text-red-300 border-red-500/30"; Icon = AlertCircle; label = "Payment Failed"; }
  return (
    <span className={"inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border text-xs font-mono uppercase tracking-wider " + bg} data-testid="track-status-badge">
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
}

function Timeline({ order }) {
  var s = (order.status || "").toLowerCase();
  var paid = s === "completed" || s === "shipped";
  var shipped = s === "shipped";

  function Step({ done, title, subtitle, IconComp }) {
    return (
      <div className="flex gap-4">
        <div className="flex flex-col items-center">
          <div className={"w-10 h-10 rounded-full flex items-center justify-center border " + (done ? "bg-[#C0A062]/15 border-[#C0A062] text-[#C0A062]" : "bg-[#1c1c1c] border-white/10 text-[#3f3f46]")}>
            <IconComp className="w-5 h-5" />
          </div>
          <div className={"w-px flex-1 mt-1 " + (done ? "bg-[#C0A062]/40" : "bg-white/5")} />
        </div>
        <div className="pb-6">
          <p className={"font-heading font-bold text-sm uppercase " + (done ? "text-white" : "text-[#A1A1AA]")}>{title}</p>
          <p className="text-[#71717a] text-xs mt-0.5">{subtitle}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="track-timeline">
      <Step done={true} title="Order Placed" subtitle={formatDate(order.created_at)} IconComp={Package} />
      <Step done={paid} title="Payment Received" subtitle={paid ? "Confirmed" : "Awaiting payment confirmation"} IconComp={CheckCircle} />
      <Step done={shipped} title="Shipped" subtitle={shipped ? formatDate(order.shipped_at) : "Preparing for shipment"} IconComp={Truck} />
    </div>
  );
}

function OrderItemRow({ item }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-[#1c1c1c] rounded-sm overflow-hidden flex-shrink-0">
        {item.image ? (
          <img src={item.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-[#3f3f46]" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm truncate">{item.name}</p>
        <p className="text-[#A1A1AA] text-xs">Qty: {item.quantity}</p>
      </div>
    </div>
  );
}

function OrderDetail({ order }) {
  var trackingUrl = carrierTrackingUrl(order.tracking_carrier, order.tracking_number);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-white/5 rounded-sm p-6 md:p-8 mt-8"
      data-testid="track-order-detail"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-[#71717a] text-xs uppercase tracking-wider mb-1">Order</p>
          <p className="font-mono text-[#C0A062] text-xl" data-testid="track-order-id">{order.order_id}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {order.status === "shipped" && order.tracking_number && (
        <div className="bg-[#0a0a0a] border border-[#8CB369]/30 rounded-sm p-4 mb-6" data-testid="track-tracking-box">
          <p className="font-mono text-[#8CB369] text-xs uppercase tracking-wider mb-2">Tracking Information</p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-white font-mono text-lg" data-testid="track-tracking-number">{order.tracking_number}</p>
              <p className="text-[#A1A1AA] text-xs">{order.tracking_carrier || "USPS"}</p>
            </div>
            {trackingUrl && (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#C0A062] text-black font-mono uppercase text-xs tracking-wider rounded-sm hover:bg-[#d4b675] transition-colors"
                data-testid="track-carrier-link"
              >
                Track with {order.tracking_carrier || "USPS"} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}

      <Timeline order={order} />

      <div className="border-t border-white/5 pt-6 mt-2">
        <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-4">Items</p>
        <div className="space-y-4">
          {(order.items || []).map(function(item, idx) {
            return <OrderItemRow key={(item.product_id || item.name || "i") + "-" + idx} item={item} />;
          })}
        </div>
      </div>

      <div className="border-t border-white/5 pt-6 mt-6">
        <div className="space-y-2">
          <div className="flex justify-between text-[#A1A1AA] text-sm">
            <span>Subtotal</span>
            <span>{formatPrice(order.subtotal)}</span>
          </div>
          {order.discount > 0 && (
            <div className="flex justify-between text-[#8CB369] text-sm">
              <span>Discount</span>
              <span>−{formatPrice(order.discount)}</span>
            </div>
          )}
          <div className="flex justify-between text-[#A1A1AA] text-sm">
            <span>Shipping</span>
            <span>{formatPrice(order.shipping)}</span>
          </div>
          <div className="flex justify-between text-white font-heading font-bold text-lg pt-2 border-t border-white/10">
            <span>Total</span>
            <span className="text-[#C0A062]">{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-white/5 text-xs text-[#71717a]">
        <p>Need help? Email <a href="mailto:admin@resurrectedgunparts.com" className="text-[#C0A062] hover:underline">admin@resurrectedgunparts.com</a> with your order number.</p>
      </div>
    </motion.div>
  );
}

export default function TrackOrderPage() {
  var [orderId, setOrderId] = useState("");
  var [email, setEmail] = useState("");
  var [result, setResult] = useState(null);
  var [error, setError] = useState("");
  var [loading, setLoading] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) {
      setError("Please enter both your Order ID and email address.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    axios.post(API + "/orders/track", { order_id: orderId.trim(), email: email.trim() })
      .then(function(res) { setResult(res.data); })
      .catch(function(err) {
        var msg = err && err.response && err.response.data && err.response.data.detail
          ? err.response.data.detail
          : "Something went wrong. Please try again.";
        setError(msg);
      })
      .finally(function() { setLoading(false); });
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="track-order-page">
      <div className="container-tactical max-w-2xl">
        <div className="mb-8">
          <p className="font-mono text-[#C0A062] text-xs uppercase tracking-wider mb-2">Order Status</p>
          <h1 className="font-heading font-black text-4xl md:text-5xl text-white uppercase mb-3">Track Your Order</h1>
          <p className="text-[#A1A1AA]">Enter the order ID from your confirmation email along with the email you used at checkout.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 rounded-sm p-6 space-y-4" data-testid="track-form">
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-xs uppercase">Order ID</Label>
            <Input
              type="text"
              value={orderId}
              onChange={function(e) { setOrderId(e.target.value); }}
              placeholder="RGP-XXXXXX"
              required
              className="input-tactical font-mono"
              data-testid="track-order-id-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[#A1A1AA] text-xs uppercase">Email Address</Label>
            <Input
              type="email"
              value={email}
              onChange={function(e) { setEmail(e.target.value); }}
              placeholder="you@example.com"
              required
              className="input-tactical"
              data-testid="track-email-input"
            />
          </div>
          <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="track-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {loading ? "Looking up your order..." : "Track Order"}
          </Button>
          {error && (
            <p className="text-red-400 text-sm flex items-start gap-2" data-testid="track-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </p>
          )}
        </form>

        {result && <OrderDetail order={result} />}

        <div className="mt-12 text-center text-[#71717a] text-sm">
          <p>Have an account? <Link to="/orders" className="text-[#C0A062] hover:underline" data-testid="track-orders-link">View all your orders</Link></p>
        </div>
      </div>
    </div>
  );
}
